// Timbre helpers: what a sound is made of, beyond its fundamental.
// Every function here is pure. Input is the analyser's dB spectrum
// (getFloatFrequencyData) plus sampleRate / binCount; output is plain data.

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Peak dB near an expected bin: max over ±halfWidth bins, then parabolic refinement. */
function peakNear(spectrum: Float32Array, centerBin: number, halfWidth: number): number {
  const lo = Math.max(1, Math.floor(centerBin - halfWidth));
  const hi = Math.min(spectrum.length - 2, Math.ceil(centerBin + halfWidth));
  if (hi < lo) return -Infinity;
  let best = lo;
  let bestDb = -Infinity;
  for (let b = lo; b <= hi; b++) {
    if (spectrum[b] > bestDb) { bestDb = spectrum[b]; best = b; }
  }
  const y0 = spectrum[best - 1], y1 = spectrum[best], y2 = spectrum[best + 1];
  const denom = y0 - 2 * y1 + y2;
  if (denom < 0) {
    const off = 0.5 * (y0 - y2) / denom;
    return y1 - 0.25 * (y0 - y2) * off;
  }
  return y1;
}

export interface Harmonic {
  k: number;      // harmonic number, 1 = fundamental
  hz: number;
  db: number;     // absolute level from the analyser
  rel: number;    // 0..1, dB distance from the loudest harmonic mapped over `rangeDb`
}

/**
 * The "recipe" of a pitched sound: level of each harmonic k × f0.
 * rel is scaled so the loudest harmonic = 1 and anything `rangeDb` quieter = 0.
 */
export function harmonicRecipe(
  spectrum: Float32Array, sampleRate: number, binCount: number,
  f0: number, count = 8, rangeDb = 50, floorDb = -100,
): Harmonic[] {
  const out: Harmonic[] = [];
  const nyquist = sampleRate / 2;
  const binsPerHz = binCount / nyquist;
  for (let k = 1; k <= count; k++) {
    const hz = f0 * k;
    if (hz >= nyquist) break;
    const cb = hz * binsPerHz;
    // Strings are slightly inharmonic: allow the peak to drift ±3 % (at least one bin).
    const hw = Math.max(1, cb * 0.03);
    const db = Math.max(floorDb, peakNear(spectrum, cb, hw));
    out.push({ k, hz, db, rel: 0 });
  }
  let maxDb = -Infinity;
  for (const h of out) if (h.db > maxDb) maxDb = h.db;
  for (const h of out) h.rel = maxDb > floorDb ? clamp01(1 + (h.db - maxDb) / rangeDb) : 0;
  return out;
}

/**
 * Pitch-class energy (C..B), 0..1 with the loudest class = 1, on a dB scale
 * spanning `rangeDb`. Sums amplitude per semitone between minHz and maxHz,
 * tilted toward lower octaves so a low note's upper harmonics do not outvote
 * the fundamentals of the other notes in a chord.
 */
export function chroma(
  spectrum: Float32Array, sampleRate: number, binCount: number,
  minHz = 70, maxHz = 2200, noiseFloorDb = -85, rangeDb = 40,
): Float32Array {
  const binHz = (sampleRate / 2) / binCount;
  const bLo = Math.max(2, Math.floor(minHz / binHz));
  const bHi = Math.min(binCount - 2, Math.ceil(maxHz / binHz));

  // 1. Spectral peaks only. A raw bin is ~11 Hz wide, which is two semitones
  //    down at low E, so summing bins smears every low note over its
  //    neighbours. Local maxima refined by parabolic interpolation give a
  //    frequency far finer than the bin width.
  const peaks: { hz: number; amp: number }[] = [];
  for (let b = bLo; b <= bHi; b++) {
    const y1 = spectrum[b];
    if (y1 <= noiseFloorDb) continue;
    const y0 = spectrum[b - 1], y2 = spectrum[b + 1];
    if (y1 < y0 || y1 < y2 || (y1 === y0 && y1 === y2)) continue;
    const denom = y0 - 2 * y1 + y2;
    let off = 0, db = y1;
    if (denom < 0) { off = 0.5 * (y0 - y2) / denom; db = y1 - 0.25 * (y0 - y2) * off; }
    peaks.push({ hz: (b + off) * binHz, amp: Math.pow(10, (db - noiseFloorDb) / 20) });
  }
  if (peaks.length === 0) return new Float32Array(12);

  // 2. Harmonic suppression: a peak that has a reasonably strong peak at
  //    1/2, 1/3, 1/4 or 1/5 of its frequency is an overtone of that lower
  //    note, not a note of its own. Keep a little of it (overtones do color
  //    the chord) but do not let it vote as a chord tone.
  const isHarmonicOf = (p: { hz: number; amp: number }) => {
    for (let k = 2; k <= 5; k++) {
      const target = p.hz / k;
      if (target < minHz * 0.9) break;
      for (const q of peaks) {
        if (q === p) continue;
        if (Math.abs(q.hz - target) / target < 0.03 && q.amp >= p.amp * 0.25) return true;
      }
    }
    return false;
  };

  const acc = new Float32Array(12);
  for (const p of peaks) {
    const midi = 12 * Math.log2(p.hz / 440) + 69;
    const pc = ((Math.round(midi) % 12) + 12) % 12;
    const tilt = Math.pow(2, -(midi - 40) / 24);   // halves every two octaves above E2
    const w = isHarmonicOf(p) ? 0.15 : 1;
    acc[pc] += p.amp * tilt * w;
  }

  let max = 0;
  for (let i = 0; i < 12; i++) if (acc[i] > max) max = acc[i];
  const out = new Float32Array(12);
  if (max <= 1) return out; // nothing above the floor
  for (let i = 0; i < 12; i++) {
    out[i] = acc[i] > 0 ? clamp01(1 + (20 * Math.log10(acc[i] / max)) / rangeDb) : 0;
  }
  return out;
}

const CHORD_TEMPLATES: { suffix: string; iv: number[] }[] = [
  { suffix: '',     iv: [0, 4, 7] },
  { suffix: 'm',    iv: [0, 3, 7] },
  { suffix: 'sus2', iv: [0, 2, 7] },
  { suffix: 'sus4', iv: [0, 5, 7] },
  { suffix: '7',    iv: [0, 4, 7, 10] },
  { suffix: 'm7',   iv: [0, 3, 7, 10] },
  { suffix: 'maj7', iv: [0, 4, 7, 11] },
  { suffix: 'dim',  iv: [0, 3, 6] },
  { suffix: 'aug',  iv: [0, 4, 8] },
  { suffix: '5',    iv: [0, 7] },
];

export interface ChordGuess {
  label: string;      // e.g. "Em", "Asus4", "E5"
  notes: string[];    // pitch classes clearly present, loudest first
  score: number;      // 0..1 template fit
}

/**
 * Template match on a display-scaled chroma vector. Returns null when fewer
 * than two pitch classes are clearly ringing (then it is a note, not a chord).
 */
export function guessChord(c: Float32Array, presentAt = 0.5): ChordGuess | null {
  const present: number[] = [];
  for (let i = 0; i < 12; i++) if (c[i] >= presentAt) present.push(i);
  if (present.length < 2) return null;

  let best: { root: number; suffix: string; score: number } | null = null;
  for (let root = 0; root < 12; root++) {
    if (c[root] < 0.3) continue; // the root has to actually be there
    for (const { suffix, iv } of CHORD_TEMPLATES) {
      let inSum = 0;
      const inSet = new Set<number>();
      for (const d of iv) { const pc = (root + d) % 12; inSet.add(pc); inSum += c[pc]; }
      let outSum = 0;
      for (let pc = 0; pc < 12; pc++) if (!inSet.has(pc)) outSum += c[pc];
      const fit = inSum / iv.length - 1.5 * (outSum / (12 - iv.length));
      const score = fit - 0.03 * Math.max(0, iv.length - 3) - (iv.length === 2 ? 0.05 : 0);
      if (!best || score > best.score) best = { root, suffix, score };
    }
  }
  if (!best || best.score < 0.3) return null;
  present.sort((a, b) => c[b] - c[a]);
  return {
    label: `${NOTE_NAMES[best.root]}${best.suffix}`,
    notes: present.map(pc => NOTE_NAMES[pc]),
    score: clamp01(best.score),
  };
}

export interface SpectralStats {
  centroidHz: number;  // where the energy sits; higher = brighter
  flatness: number;    // 0 = pure tone, 1 = white noise
  energy: number;      // summed linear power above the floor
}

export function spectralStats(
  spectrum: Float32Array, sampleRate: number, binCount: number,
  minHz = 60, maxHz = 6000, floorDb = -90,
): SpectralStats {
  const binHz = (sampleRate / 2) / binCount;
  const bLo = Math.max(1, Math.floor(minHz / binHz));
  const bHi = Math.min(binCount - 1, Math.ceil(maxHz / binHz));
  let sumP = 0, sumPF = 0, sumLogP = 0, n = 0;
  for (let b = bLo; b <= bHi; b++) {
    const p = Math.pow(10, (Math.max(spectrum[b], floorDb) - floorDb) / 10); // floor = 1
    sumP += p; sumPF += p * b * binHz; sumLogP += Math.log(p); n++;
  }
  if (n === 0 || sumP === 0) return { centroidHz: 0, flatness: 1, energy: 0 };
  return {
    centroidHz: sumPF / sumP,
    flatness: Math.exp(sumLogP / n) / (sumP / n),
    energy: sumP - n,
  };
}

/** Brightness meter 0..1: spectral centroid on a log scale, 300 Hz → 0, ~3.4 kHz → 1. */
export const brightness = (s: SpectralStats) =>
  s.centroidHz > 0 ? clamp01(Math.log2(s.centroidHz / 300) / 3.5) : 0;

/** Breathiness meter 0..1: spectral flatness on a log scale, 0.001 → 0, 1 → 1. */
export const breathiness = (s: SpectralStats) =>
  clamp01((Math.log10(Math.max(s.flatness, 1e-6)) + 3) / 3);

// ── Two sources together ────────────────────────────────────────────────────

const INTERVAL_NAMES = ['unison', 'minor 2nd', 'major 2nd', 'minor 3rd', 'major 3rd', 'perfect 4th',
  'tritone', 'perfect 5th', 'minor 6th', 'major 6th', 'minor 7th', 'major 7th'];
const INTERVAL_SHORT = ['P1', 'm2', 'M2', 'm3', 'M3', 'P4', 'TT', 'P5', 'm6', 'M6', 'm7', 'M7'];
// Just intonation ratios for the same 12 classes (5-limit, tritone 45/32, m7 as 16/9)
const JUST_RATIOS = [1, 16 / 15, 9 / 8, 6 / 5, 5 / 4, 4 / 3, 45 / 32, 3 / 2, 8 / 5, 5 / 3, 16 / 9, 15 / 8];

export interface IntervalInfo {
  semitones: number;      // signed, rounded: b relative to a
  octaves: number;        // whole octaves in the distance
  cls: number;            // 0..11 interval class
  name: string;           // e.g. "major 3rd"
  short: string;          // e.g. "M3"
  centsEqual: number;     // deviation from equal temperament, signed
  centsJust: number;      // deviation from the just ratio, signed
  direction: 'above' | 'below' | 'same';
}

/** Musical interval from a to b (Hz). */
export function intervalInfo(aHz: number, bHz: number): IntervalInfo {
  const exact = 12 * Math.log2(bHz / aHz);
  const semis = Math.round(exact);
  const centsEqual = Math.round((exact - semis) * 100) || 0; // `|| 0` turns -0 into 0
  const abs = Math.abs(semis);
  const cls = abs % 12;
  const octaves = Math.floor(abs / 12);
  const justRatio = JUST_RATIOS[cls] * Math.pow(2, octaves);
  const ratio = Math.pow(2, Math.abs(exact) / 12);
  const centsJust = Math.round(1200 * Math.log2(ratio / justRatio)) || 0;
  let name = INTERVAL_NAMES[cls];
  if (cls === 0 && octaves > 0) name = octaves === 1 ? 'octave' : `${octaves} octaves`;
  else if (octaves > 0) name = `${name} + ${octaves} oct`;
  return {
    semitones: semis, octaves, cls, name, short: INTERVAL_SHORT[cls],
    centsEqual, centsJust,
    direction: semis === 0 && Math.abs(exact) < 0.5 ? 'same' : exact > 0 ? 'above' : 'below',
  };
}

export interface Partial { hz: number; amp: number; }

/** Partials (linear amplitude) of a pitched sound: harmonics k × f0 read off the spectrum. */
export function partialsOf(spectrum: Float32Array, sampleRate: number, binCount: number, f0: number, count = 8): Partial[] {
  return harmonicRecipe(spectrum, sampleRate, binCount, f0, count, 60)
    .map(h => ({ hz: h.hz, amp: Math.pow(10, h.db / 20) }));
}

/**
 * Sensory roughness between two sets of partials, Plomp & Levelt curve in
 * Sethares' parameterisation. 0 = every partial pair is either far apart or
 * exactly coincident; 1 = every pair sits at the roughest spacing. Two notes
 * a just fifth apart come out low, a semitone apart comes out high.
 */
export function roughness(a: Partial[], b: Partial[]): number {
  const D_MAX = 0.188; // peak of exp(-3.5x) - exp(-5.75x)
  let num = 0, den = 0;
  for (const p of a) for (const q of b) {
    const fmin = Math.min(p.hz, q.hz), fmax = Math.max(p.hz, q.hz);
    const s = 0.24 / (0.021 * fmin + 19);
    const x = s * (fmax - fmin);
    const d = Math.exp(-3.5 * x) - Math.exp(-5.75 * x);
    const w = p.amp * q.amp;
    num += w * d; den += w;
  }
  return den > 0 ? clamp01(num / (den * D_MAX)) : 0;
}

/**
 * Pitch wobble over the last `frames` samples of a pitch history ring buffer:
 * half the peak-to-peak deviation in cents. Returns null unless the whole
 * window is voiced (no gaps).
 */
export function pitchWobbleCents(
  history: ({ hz: number } | null)[], headIdx: number, frames = 40,
): number | null {
  const len = history.length;
  if (frames > len) frames = len;
  let sumLog = 0;
  const logs: number[] = [];
  for (let i = 1; i <= frames; i++) {
    const p = history[(headIdx - i + len) % len];
    if (!p || p.hz <= 0) return null;
    const l = Math.log2(p.hz);
    logs.push(l); sumLog += l;
  }
  const mean = sumLog / logs.length;
  let lo = Infinity, hi = -Infinity;
  for (const l of logs) {
    const cents = (l - mean) * 1200;
    if (cents < lo) lo = cents;
    if (cents > hi) hi = cents;
  }
  return (hi - lo) / 2;
}
